import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Bot, Eye, EyeOff, LayoutGrid, ReceiptText, RotateCcw, UserRound } from 'lucide-react';
import { toast } from 'sonner';
import { z } from 'zod';
import { formatDate } from '@/shared/lib/format';
import { isMasterEmail } from '@/shared/lib/navigation';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Checkbox } from '@/shared/ui/checkbox';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { SegmentedControl } from '@/shared/ui/segmented-control';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/shared/ui/sheet';
import { Skeleton } from '@/shared/ui/skeleton';
import { Switch } from '@/shared/ui/switch';
import { Tooltip } from '@/shared/ui/tooltip';
import { useConfirm } from '@/shared/components/confirm';
import { useResetUserUsage, useUpdateAiUser } from '@/features/ai-credits/api/use-ai-admin';
import { formatTokens, formatUsd } from '@/features/ai-credits/model/format';
import type { AiUserRow } from '@/features/ai-credits/model/types';
import { AiSettingsForm } from '@/features/ai-credits/ui/ai-settings-form';
import { CreditsBar } from '@/features/ai-credits/ui/credits-bar';
import { UsageDialog } from '@/features/ai-credits/ui/usage-dialog';
import {
  useCreatePassbookUser,
  usePassbookStoreOptions,
  usePermissionPassbook,
  type PassbookPermissionRow,
} from '@/features/permissions/api/use-permissions';
import { UserPageFlags } from '@/features/permissions/ui/user-page-flags';
import type { StoreCatalogItem } from '../model/linked-stores';
import { CREATABLE_PANEL_ROLES, panelRoleLabel } from '../model/roles';

const schema = z.object({
  name: z.string().trim().min(1, 'Digite o nome'),
  email: z.string().trim().min(1, 'Digite o e-mail').email('E-mail inválido'),
  password: z
    .string()
    .refine((value) => value === '' || value.length >= 6, 'Mínimo de 6 caracteres')
    .refine((value) => !value.startsWith('_'), 'Não pode começar com _'),
  role: z.string().min(1, 'Selecione o perfil'),
  stores: z.array(z.string()),
});

type FormValues = z.infer<typeof schema>;
type SheetTab = 'perfil' | 'paginas' | 'ia';

const TABS = [
  { value: 'perfil' as const, label: 'Perfil', icon: UserRound },
  { value: 'paginas' as const, label: 'Páginas', icon: LayoutGrid },
  { value: 'ia' as const, label: 'IA', icon: Bot },
];

interface UserSheetProps {
  open: boolean;
  /** `null` cria um usuário novo no `passbookId`. */
  user: AiUserRow | null;
  /** Passbook de contexto. Na edição, cai no passbook do próprio usuário. */
  passbookId?: string;
  onClose: () => void;
  onSaved: (row: AiUserRow) => void;
}

/**
 * Um editor só para o usuário do painel, em três abas: Perfil, Páginas e IA.
 * Substitui os três diálogos anteriores e o de cadastro.
 */
export function UserSheet({ open, user, passbookId, onClose, onSaved }: UserSheetProps) {
  const contextPassbookId = passbookId ?? user?.passbook?.id ?? null;
  const { data: passbook } = usePermissionPassbook(open ? contextPassbookId : null);
  const { data: stores } = usePassbookStoreOptions(passbook ?? ({ id: '', storeCount: 0 } as PassbookPermissionRow));
  const storeCatalog = useMemo<StoreCatalogItem[]>(
    () => (stores?.rows ?? []).filter((store) => store.storeCode.trim()),
    [stores?.rows],
  );
  const [tab, setTab] = useState<SheetTab>('perfil');
  useEffect(() => {
    if (open) setTab('perfil');
  }, [open, user?.id]);

  const creating = open && !user;
  const locked = Boolean(user && isMasterEmail(user.email));

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent className="sm:w-[44rem] sm:max-w-[44rem]">
        <SheetHeader>
          <SheetTitle className="flex flex-wrap items-center gap-2">
            {creating ? 'Novo usuário' : user?.name || user?.email}
            {user ? (
              <>
                <Badge variant={user.active ? 'success' : 'destructive'}>{user.active ? 'Ativo' : 'Inativo'}</Badge>
                <Badge variant="secondary" className="font-normal">
                  {panelRoleLabel(user.role)}
                </Badge>
              </>
            ) : null}
          </SheetTitle>
          {user?.email || passbook?.name ? (
            <p className="text-sm text-muted-foreground">
              {[user?.email, user?.passbook?.name ?? passbook?.name].filter(Boolean).join(' · ')}
            </p>
          ) : null}
        </SheetHeader>

        {creating && !contextPassbookId ? (
          <p className="text-sm text-muted-foreground">Abra um cliente para cadastrar o usuário nele.</p>
        ) : null}

        {creating && contextPassbookId ? (
          <ProfileForm key="new" passbookId={contextPassbookId} storeCatalog={storeCatalog} onSaved={onSaved} />
        ) : null}

        {user ? (
          <div className="space-y-5">
            <SegmentedControl value={tab} onChange={setTab} options={TABS} size="sm" fill />

            {tab === 'perfil' ? (
              locked ? (
                <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
                  <dt className="text-muted-foreground">E-mail</dt>
                  <dd className="truncate">{user.email}</dd>
                  <dt className="text-muted-foreground">Perfil</dt>
                  <dd>{panelRoleLabel(user.role)}</dd>
                  <dt className="text-muted-foreground">Lojas</dt>
                  <dd>{user.stores.length === 0 ? 'Todas' : user.stores.map((s) => s.name).join(', ')}</dd>
                </dl>
              ) : (
                <ProfileForm
                  key={user.id}
                  user={user}
                  passbookId={contextPassbookId ?? undefined}
                  storeCatalog={storeCatalog}
                  onSaved={onSaved}
                />
              )
            ) : null}

            {tab === 'paginas' ? (
              passbook ? (
                <UserPageFlags passbook={passbook} user={user} onSaved={onSaved} />
              ) : (
                <Skeleton className="h-64 rounded-2xl" />
              )
            ) : null}

            {tab === 'ia' ? <AiTab user={user} passbookId={contextPassbookId ?? undefined} onSaved={onSaved} /> : null}
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

function ProfileForm({
  user,
  passbookId,
  storeCatalog,
  onSaved,
}: {
  user?: AiUserRow;
  passbookId?: string;
  storeCatalog: StoreCatalogItem[];
  onSaved: (row: AiUserRow) => void;
}) {
  const update = useUpdateAiUser(passbookId);
  const create = useCreatePassbookUser(passbookId ?? '');
  const [showPassword, setShowPassword] = useState(false);
  const [storeQuery, setStoreQuery] = useState('');
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: user?.name ?? '',
      email: user?.email ?? '',
      password: '',
      role: user?.role ?? 'Client',
      stores: user?.storeCodes ?? [],
    },
  });

  const storeKey = (user?.storeCodes ?? []).join('|');
  useEffect(() => {
    setShowPassword(false);
    setStoreQuery('');
    form.reset({
      name: user?.name ?? '',
      email: user?.email ?? '',
      password: '',
      role: user?.role ?? 'Client',
      stores: storeKey ? storeKey.split('|') : [],
    });
  }, [user?.id, user?.name, user?.email, user?.role, storeKey, form]);

  const roleOptions = useMemo(() => {
    const creatable = CREATABLE_PANEL_ROLES as readonly string[];
    if (!user || creatable.includes(user.role)) return [...creatable];
    return [user.role, ...creatable];
  }, [user]);

  const visibleStores = useMemo(() => {
    const q = storeQuery.trim().toLowerCase();
    if (!q) return storeCatalog;
    return storeCatalog.filter(
      (store) => store.name.toLowerCase().includes(q) || store.storeCode.toLowerCase().includes(q),
    );
  }, [storeCatalog, storeQuery]);

  const submit = form.handleSubmit(async (values) => {
    if (!user && values.password.length < 6) {
      form.setError('password', { message: 'Mínimo de 6 caracteres' });
      return;
    }
    try {
      if (user) {
        const row = await update.mutateAsync({
          id: user.id,
          name: values.name.trim(),
          email: values.email.trim(),
          role: values.role,
          stores: values.stores,
          ...(values.password ? { password: values.password } : {}),
        });
        onSaved(row);
        toast.success('Perfil salvo.');
      } else {
        const row = await create.mutateAsync({
          name: values.name.trim(),
          email: values.email.trim(),
          password: values.password,
          role: values.role,
          stores: values.stores,
        });
        onSaved(row);
        toast.success('Usuário cadastrado.');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não deu para salvar.');
    }
  });

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Nome" error={form.formState.errors.name?.message}>
          <Input autoComplete="off" {...form.register('name')} />
        </Field>
        <Field label="E-mail" error={form.formState.errors.email?.message}>
          <Input type="email" autoComplete="off" {...form.register('email')} />
        </Field>
        <Field label={user ? 'Nova senha' : 'Senha'} error={form.formState.errors.password?.message}>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              placeholder={user ? 'Em branco, mantém a atual' : 'Mínimo de 6 caracteres'}
              className="pr-10"
              {...form.register('password')}
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              className="absolute right-1 top-1/2 -translate-y-1/2"
              onClick={() => setShowPassword((value) => !value)}
              aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            >
              {showPassword ? <EyeOff /> : <Eye />}
            </Button>
          </div>
        </Field>
        <Field label="Perfil" error={form.formState.errors.role?.message}>
          <Controller
            control={form.control}
            name="role"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.map((value) => (
                    <SelectItem key={value} value={value}>
                      {panelRoleLabel(value)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Field>
      </div>

      {storeCatalog.length > 0 ? (
        <Controller
          control={form.control}
          name="stores"
          render={({ field }) => {
            const all = storeCatalog.map((store) => store.storeCode);
            const count = field.value.length;
            return (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label>
                    Lojas{' '}
                    <span className="font-normal text-muted-foreground">
                      · {count === 0 ? 'todas' : `${count} de ${all.length}`}
                    </span>
                  </Label>
                  <div className="flex gap-1">
                    <Button type="button" variant="ghost" size="sm" onClick={() => field.onChange(all)}>
                      Todas
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => field.onChange([])}>
                      Limpar
                    </Button>
                  </div>
                </div>
                {storeCatalog.length > 6 ? (
                  <Input
                    value={storeQuery}
                    onChange={(event) => setStoreQuery(event.target.value)}
                    placeholder="Buscar loja"
                    autoComplete="off"
                  />
                ) : null}
                <div className="grid max-h-44 gap-x-4 gap-y-1 overflow-y-auto rounded-xl border border-border p-2 sm:grid-cols-2">
                  {visibleStores.length === 0 ? (
                    <p className="px-2 py-2 text-sm text-muted-foreground sm:col-span-2">Nenhuma loja com esse nome.</p>
                  ) : (
                    visibleStores.map((store) => {
                      const checked = field.value.includes(store.storeCode);
                      const id = `user-store-${store.storeCode}`;
                      return (
                        <div key={store.storeCode} className="flex min-w-0 items-center gap-2 py-0.5">
                          <Checkbox
                            id={id}
                            checked={checked}
                            onCheckedChange={(value) => {
                              const next =
                                value === true
                                  ? [...field.value, store.storeCode]
                                  : field.value.filter((code) => code !== store.storeCode);
                              field.onChange(next);
                            }}
                          />
                          <Label htmlFor={id} className="min-w-0 truncate font-normal">
                            {store.name}
                          </Label>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          }}
        />
      ) : null}

      <div className="flex justify-end">
        <Button type="submit" loading={form.formState.isSubmitting}>
          {user ? 'Salvar perfil' : 'Cadastrar'}
        </Button>
      </div>
    </form>
  );
}

function AiTab({
  user,
  passbookId,
  onSaved,
}: {
  user: AiUserRow;
  passbookId?: string;
  onSaved: (row: AiUserRow) => void;
}) {
  const update = useUpdateAiUser(passbookId);
  const reset = useResetUserUsage();
  const confirm = useConfirm();
  const [usageOpen, setUsageOpen] = useState(false);
  const master = isMasterEmail(user.email);

  const toggleAdvanced = (next: boolean) => {
    update.mutate(
      { id: user.id, hasCampaignAdvanced: next },
      {
        onSuccess: (row) => onSaved(row),
        onError: () => toast.error('Não deu para alterar.'),
      },
    );
  };

  const resetUsage = async () => {
    const ok = await confirm({
      title: 'Zerar consumo do período?',
      description: 'O limite volta inteiro agora. O histórico continua.',
      confirmLabel: 'Zerar',
    });
    if (!ok) return;
    reset.mutate(user.id, {
      onSuccess: (row) => {
        onSaved(row);
        toast.success('Consumo zerado.');
      },
      onError: () => toast.error('Não deu para zerar.'),
    });
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-border/60 bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-2xl font-semibold tabular-nums tracking-tight">
              {formatUsd(user.credits.usedUsd)}
              {master ? null : <span className="text-base font-normal text-muted-foreground"> de {formatUsd(user.credits.limitUsd)}</span>}
            </p>
            <p className="text-xs text-muted-foreground">
              {master ? 'sem limite' : user.renewsAt ? `renova ${formatDate(user.renewsAt)}` : 'não renova'}
              {' · '}
              {formatTokens(user.tokens.input + user.tokens.output)} tokens
              {' · '}
              último uso {formatDate(user.lastUsedAt, 'nunca')}
            </p>
          </div>
          <div className="flex gap-1">
            <Button variant="secondary" size="sm" loading={reset.isPending} onClick={() => void resetUsage()}>
              <RotateCcw />
              Zerar
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setUsageOpen(true)}>
              <ReceiptText />
              Extrato
            </Button>
          </div>
        </div>
        {master ? null : (
          <CreditsBar className="mt-3" percentUsed={user.credits.percentUsed} exhausted={user.credits.exhausted} />
        )}
      </div>

      <div className="rounded-2xl border border-border/60 bg-card p-4">
        <AiSettingsForm
          level="user"
          value={user.aiSettings}
          inherited={user.effective}
          saving={update.isPending}
          onSave={(aiSettings) =>
            update.mutateAsync(
              { id: user.id, aiSettings },
              {
                onSuccess: (row) => {
                  onSaved(row);
                  toast.success('IA salva.');
                },
                onError: () => toast.error('Não deu para salvar a IA.'),
              },
            )
          }
        />
      </div>

      <div className="flex items-center justify-between gap-3 rounded-2xl border border-border/60 bg-card px-4 py-3">
        <span className="flex min-w-0 items-center gap-1.5 text-sm">
          <Label htmlFor={`adv-${user.id}`} className="font-normal leading-normal">
            Campanhas automatizadas do usuário
          </Label>
          <Tooltip content="Flag do próprio usuário: libera /owner/campanhas para Owner e o atalho de campanhas sem loja carregada." />
        </span>
        <Switch
          id={`adv-${user.id}`}
          checked={user.hasCampaignAdvanced}
          disabled={update.isPending}
          onCheckedChange={toggleAdvanced}
        />
      </div>

      <UsageDialog open={usageOpen} onOpenChange={setUsageOpen} title={`Extrato de ${user.name}`} filter={{ userId: user.id }} />
    </div>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {error ? <p className="text-xs text-destructive">{error}</p> : null}
    </div>
  );
}
